/**
 * FormFieldWithOptions Component
 *
 * Renders a single custom question field with all editing controls.
 * Used within AdditionalQuestions to display each question in the list.
 *
 * ## Used By
 * - AdditionalQuestions (renders array of these)
 * - All survey types (Custom, Event, Satisfaction, Donation)
 *
 * ## Features
 * - **Question Label Input**: Text input for question text
 * - **Field Type Selector**: Dropdown to choose question type (Short Answer, Dropdown, Checkboxes)
 * - **Required/Optional Toggle**: Checkbox to mark question as required
 * - **Options Management**: Add/remove/edit options for Dropdown and Checkboxes types
 * - **Validation**: Real-time validation with error messages
 * - **Conditional Rendering**: Shows/hides options based on field type
 *
 * ## Field Types
 * - **Short Answer (String)**: Single-line text input for respondents
 * - **Dropdown (Option)**: Single-selection from list of options
 * - **Checkboxes (MultipleOptions)**: Multi-selection from list of options
 *
 * ## Architecture
 * Decomposed into focused sub-components:
 * - **FieldHeader**: Question label input + required/optional toggle
 * - **FieldTypeSelector**: Dropdown to change field type
 * - **FieldInput**: Individual option input (for Dropdown/Checkboxes)
 * - **FieldOptions**: Container managing all options
 *
 * ## Integration
 * Must be used inside FormProvider. Parent (AdditionalQuestions) manages:
 * - Field CRUD operations
 * - Form state updates
 * - Validation triggers
 */

import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { ISelectBoxOption } from '../../../../../components/SelectBox';
import { bs } from '../../../../../styles/styles';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { FieldHeader, FieldInput, FieldOptions, FieldTypeSelector } from './components';
import { FileFieldPlaceholder } from './components/FileFieldPlaceholder';
import { QuestionType, currentQuestionType, questionTypeOptions } from './questionTypes';
import { styleSheet } from './styles';
import { useFieldTypeChange } from './useFieldTypeChange';

/**
 * Default question-type options for the dropdown.
 *
 * Keyed by QuestionType (the BE `_type`), not by FormFieldType — so two question
 * types can share a backing FormFieldType without colliding. Order preserved from
 * the historical default (Short Answer, Checkboxes, Dropdown). Override via the
 * `formFieldOptions` prop for survey-specific sets.
 */
export const FormFieldOptions = questionTypeOptions([
	QuestionType.ShortAnswer,
	QuestionType.CheckBoxes,
	QuestionType.DropDown,
]);

/**
 * Props for FormFieldWithOptions component
 */
interface Props {
	/**
	 * The field object containing question data
	 *
	 * Properties:
	 * - id: Unique identifier
	 * - label: Question text
	 * - fieldType: Type (String, Option, MultipleOptions)
	 * - isOptional: Whether question is optional
	 * - options: Array of choices (for Dropdown/Checkboxes)
	 */
	field: Api.IFormField<string>;

	/**
	 * Position in the fields array
	 *
	 * Used for:
	 * - Array updates when field changes
	 * - Error message lookup (e.g., customForm.fields[2].label)
	 * - Unique keys for React rendering
	 */
	questionIndex: number;

	/**
	 * Current field value (typically question label)
	 *
	 * Used for controlled input synchronization.
	 */
	value: string;

	/**
	 * Handler for required/optional toggle
	 *
	 * Called when user clicks the "Required" checkbox.
	 * Typically updates field.isOptional property.
	 *
	 * @param index - Question index in array
	 */
	onToggleCheckChanged?: (index: number) => void;

	/**
	 * Handler for question label changes
	 *
	 * Called as user types in the question text input.
	 *
	 * @param e - Input change event
	 * @param index - Question index in array
	 */
	onChange?: (e: React.ChangeEvent<HTMLInputElement>, index: number) => void;

	/**
	 * Custom renderer for field type selector trigger button
	 *
	 * Allows customizing the dropdown trigger display.
	 * Default shows selected type title (e.g., "Short Answer").
	 */
	onRenderTriggerTitle?: (selectedOption: ISelectBoxOption<QuestionType>) => React.ReactNode;

	/**
	 * Handler for field type changes
	 *
	 * Called when user selects different type from dropdown.
	 * Should update field.fieldType and handle type-specific logic
	 * (e.g., clear options when changing to Short Answer).
	 */
	handleFormFieldTypeChange?: (selectedType: ISelectBoxOption<QuestionType>, questionIndex: number) => void;

	/**
	 * Handler for field updates
	 *
	 * Called whenever field data changes (label, type, options, etc.).
	 * Parent should update form state and mark field as dirty.
	 *
	 * @param field - Updated field object
	 * @param index - Question index in array
	 */
	onUpdateField: (field: Api.IFormField<string>, index: number) => void;

	/**
	 * Whether editing existing survey (vs creating new)
	 *
	 * Used to conditionally show certain features or validations.
	 */
	isEdit: boolean;

	/**
	 * Label for the field (optional override)
	 *
	 * Defaults to empty string.
	 * Can be used for custom labeling in specific contexts.
	 */
	label?: string;

	/**
	 * Whether to disable all inputs
	 *
	 * When true, field becomes read-only (e.g., during save operation).
	 */
	disabledInputs?: boolean;

	/**
	 * Whether this field is already persisted to the backend.
	 *
	 * When provided, drives whether the type-selector dropdown is disabled
	 * (saved fields can't change type). When omitted, falls back to the
	 * `!!field._type` heuristic — accurate for V1 customForm.fields but
	 * NOT for V2 surveyParts questions, which are seeded with `_type` at
	 * creation time. V2 callers MUST pass this prop explicitly.
	 */
	hasBeenSaved?: boolean;

	/**
	 * Placeholder text for question label input
	 *
	 * Shown when question label is empty.
	 * Example: "Enter question text"
	 */
	inputPlaceholder?: string;

	/**
	 * Custom field type options (overrides default)
	 *
	 * Allows restricting available field types per survey.
	 * Defaults to all three types (String, Option, MultipleOptions).
	 *
	 * Example for Custom Survey (only String and Option):
	 * ```tsx
	 * formFieldOptions={[
	 *   { title: 'Short Answer', value: FormFieldType.String },
	 *   { title: 'Dropdown', value: FormFieldType.Option }
	 * ]}
	 * ```
	 */
	formFieldOptions?: Array<{ title: string; value: QuestionType }>;

	/**
	 * Custom content to render after each option
	 *
	 * Used for survey-specific features like tag mapping indicators.
	 * Receives option label and returns React node to display.
	 *
	 * Example (Custom Survey tag indicator):
	 * ```tsx
	 * onAfterItem={(label) => <TagIcon />}
	 * ```
	 */
	onAfterItem?: (optionLabel: string) => React.ReactNode;
	/**
	 * Optional pre-resolved RHF errors for this question.
	 * V2 sectioned editor injects errors from `surveyForm.surveyParts.[i].questions.[j]`
	 * via this prop. When present, overrides the V1 `customForm.fields.[index]` lookup.
	 * When absent, the V1 lookup is used as-is (no behaviour change for V1).
	 */
	questionErrors?: {
		label?: { message?: string };
		options?: { message?: string };
	};
}

/**
 * FormFieldErrorMessage Component
 *
 * Simple error message display for form validation errors.
 * Shows red text below field when validation fails.
 *
 * @param errorMessage - Error text to display (if any)
 */
export const FormFieldErrorMessage = ({ errorMessage }: { errorMessage?: string }) => {
	if (!errorMessage) {
		return null;
	}
	return <p className={css(styleSheet.errorMessageInline)}>{errorMessage}</p>;
};

/**
 * FormFieldWithOptions component implementation
 *
 * Memoized with React.memo to prevent unnecessary re-renders when parent updates.
 * Only re-renders when props actually change.
 *
 * @example Basic usage in AdditionalQuestions
 * ```tsx
 * <FormFieldWithOptions
 *   field={field}
 *   questionIndex={index}
 *   value={field.label}
 *   onUpdateField={handleFieldUpdate}
 *   isEdit={isEdit}
 *   inputPlaceholder="Enter your question"
 * />
 * ```
 */
const FormFieldWithOptionsInner = React.memo<React.PropsWithChildren<Props>>(
	({
		field,
		questionIndex,
		value,
		onToggleCheckChanged,
		onChange,
		onRenderTriggerTitle,
		handleFormFieldTypeChange,
		onUpdateField,
		isEdit,
		children,
		label = '',
		inputPlaceholder = '',
		disabledInputs = false,
		hasBeenSaved: hasBeenSavedProp,
		formFieldOptions = FormFieldOptions,
		onAfterItem,
		questionErrors,
	}) => {
		// Get preview mode from context
		const { isPreviewOnly } = usePreviewMode();

		// Calculate required status (inverse of isOptional)
		const isRequired = !field?.isOptional;

		// Get form context for error handling and validation
		const { clearErrors, formState } = useFormContext<Api.IEventRegistrationSurvey | Api.ISatisfactionSurvey>();

		// Custom hook handles field type changes
		// Clears validation errors when type changes (e.g., String → Option)
		useFieldTypeChange({
			field,
			questionIndex,
			onUpdateField,
			clearErrors,
		});

		// Memoize options array to prevent unnecessary re-renders
		// Options only change when user adds/removes/edits options
		const fieldOptions = React.useMemo(() => field.options || [], [field.options]);

		// Check if field has been saved to backend
		// Used to determine if this is a new unsaved field.
		// V2 callers pass `hasBeenSavedProp` explicitly (they track persisted
		// IDs in a ref); V1 callers omit it and fall back to the `_type`
		// heuristic, which is accurate for `customForm.fields[]` but NOT for
		// V2 `surveyParts.questions[]` (those carry `_type` from creation).
		const hasBeenSaved = React.useMemo(() => hasBeenSavedProp ?? !!field?._type, [hasBeenSavedProp, field?._type]);

		// Derive error message from form state.
		// react-hook-form's proxy tracks which error properties are accessed during render,
		// so the component re-renders automatically when relevant errors change.
		// When `questionErrors` is provided (V2 sectioned editor), it takes precedence over
		// the V1 `customForm.fields.[index]` path; V1 callers omit the prop and fall through
		// to the original lookup unchanged.
		const errorMessage = React.useMemo(() => {
			const optionsError =
				questionErrors?.options?.message ?? formState.errors?.customForm?.fields?.[questionIndex]?.options?.message;
			const labelError =
				questionErrors?.label?.message ?? formState.errors?.customForm?.fields?.[questionIndex]?.label?.message;
			const conditionalError =
				'customFormMetaData' in formState.errors
					? formState.errors?.customFormMetaData?.[questionIndex]?.isConditional?.message
					: undefined;
			const visibilityError =
				'surveyFormVisibilityValidation' in formState.errors
					? (formState.errors as any)?.surveyFormVisibilityValidation?.[questionIndex]?.message
					: undefined;

			return optionsError || labelError || conditionalError || visibilityError;
		}, [formState.errors, questionIndex, questionErrors]);

		// Optimize callback dependencies with error handling
		const handleOptionChange = React.useCallback(
			(e: React.ChangeEvent<HTMLInputElement>, index: number) => {
				try {
					const newOptions = [...fieldOptions];
					newOptions[index] = {
						...newOptions[index],
						label: e.target.value,
					};

					const updatedField = {
						...field,
						options: newOptions,
					};

					// Clear validation error if field now has both label and options content
					const hasLabel = updatedField.label?.trim();
					const hasOptions =
						updatedField.options.every(x => Boolean(x.label)) &&
						updatedField.options.length >= 2 &&
						(updatedField.fieldType === Api.FormFieldType.Option ||
							updatedField.fieldType === Api.FormFieldType.MultipleOptions);

					if (hasLabel && hasOptions) {
						clearErrors(`customForm.fields.${questionIndex}.options`);
						clearErrors(`customForm.fields.${questionIndex}.label`);
					}

					onUpdateField(updatedField, questionIndex);
				} catch (error) {
					console.error('Error in handleOptionChange:', error);
				}
			},
			[clearErrors, fieldOptions, field, onUpdateField, questionIndex]
		);

		const handleRemoveOption = React.useCallback(
			(index: number) => {
				try {
					const newOptions = [...fieldOptions];
					newOptions.splice(index, 1);

					const updatedField = {
						...field,
						options: newOptions,
					};
					onUpdateField(updatedField, questionIndex);
				} catch (error) {
					console.error('Error in handleOptionChange:', error);
				}
			},
			[fieldOptions, field, onUpdateField, questionIndex]
		);

		const handleAddOption = React.useCallback(() => {
			try {
				const newOptions = [...fieldOptions];
				newOptions.push({ id: crypto.randomUUID(), fieldId: field?.id, label: '' });

				const updatedField = {
					...field,
					options: newOptions,
				};
				onUpdateField(updatedField, questionIndex);
			} catch (error) {
				console.error('Error in handleAddOption:', error);
			}
		}, [fieldOptions, field, onUpdateField, questionIndex]);

		const handleMoveOption = React.useCallback(
			(fromIndex: number, toIndex: number) => {
				try {
					const newOptions = [...fieldOptions];
					const [movedOption] = newOptions.splice(fromIndex, 1);
					newOptions.splice(toIndex, 0, movedOption);

					const updatedField = {
						...field,
						options: newOptions,
					};
					onUpdateField(updatedField, questionIndex);
				} catch (error) {
					console.error('Error in handleMoveOption:', error);
				}
			},
			[fieldOptions, field, onUpdateField, questionIndex]
		);

		// Combine disabledInputs prop with preview mode
		const isDisabled = disabledInputs || isPreviewOnly;

		return (
			<>
				<FieldHeader
					fieldId={field?.id}
					questionIndex={questionIndex}
					label={label}
					isRequired={isRequired}
					onToggleCheckChanged={onToggleCheckChanged}
					disabled={isDisabled}
				/>
				<div className={css(bs.flex, bs.flexBetween, bs.gap9, bs.itemsStart)}>
					<div className={css(bs.flex, bs.flexCol, bs.fullWidth)}>
						<FieldInput
							fieldId={field.id}
							questionIndex={questionIndex}
							value={value}
							placeholder={inputPlaceholder}
							disabled={isDisabled}
							onChange={onChange}
							errorMessage={errorMessage}
						/>
						<FieldOptions
							field={field}
							questionIndex={questionIndex}
							onOptionChange={handleOptionChange}
							onRemoveOption={handleRemoveOption}
							onAddOption={handleAddOption}
							onMoveOption={handleMoveOption}
							onAfterItem={onAfterItem}
							disabled={isDisabled}
						/>
						<FileFieldPlaceholder field={field} />
					</div>
					<FieldTypeSelector
						selectedType={currentQuestionType(field)}
						questionIndex={questionIndex}
						isEdit={isEdit}
						hasBeenSaved={hasBeenSaved}
						disabledInputs={isDisabled}
						formFieldOptions={formFieldOptions}
						onRenderTriggerTitle={onRenderTriggerTitle}
						onSelectionChanged={handleFormFieldTypeChange}
					/>
				</div>
				{children}
			</>
		);
	}
);

FormFieldWithOptionsInner.displayName = 'FormFieldWithOptionsInner';

export const FormFieldWithOptions = FormFieldWithOptionsInner;

FormFieldWithOptions.displayName = 'FormFieldWithOptions';
